import React, { useEffect, useState } from "react";
import SEO from "../components/SEO";
import GallerySection from "../components/GallerySection";
import ContactForm from "../components/ContactForm";
import { getCMSCollection, getOptimizedImageUrl, getGalleryByRef } from "../lib/cms";

export default function Raadgivning() {
  const [galleries, setGalleries] = useState([]);

  useEffect(() => {
    async function loadData() {
      const g = await getCMSCollection("galleries");
      setGalleries(g);
    }
    loadData();
  }, []);

  const gallery = getGalleryByRef(galleries, "gallery-raadgivning") || getGalleryByRef(galleries, "raadgivning");
  const galleryImages = gallery?.images || [];

  return (
    <>
      <SEO
        title="Rådgivning"
        description="Jeg tilbyder vurdering af træer, hvor du er i tvivl om træets tilstand eller måske frygter at det vælter eller grene brækker af. Jeg råder og vejleder til den bedste løsning, der nødvendigvis ikke altid er at fælde træet."
        ogImage="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/540320308-772834888876748-8893434418948825966-n_c81ca2ea.jpeg"
      />

      {/* Hero Header */}
      <section className="relative bg-[#12281D] text-white py-16 sm:py-24 border-b border-[#1E4231] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/540320308-772834888876748-8893434418948825966-n.jpeg",
              1600,
              80
            )}
            alt="Rådgivning om træer"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12281D] via-[#12281D]/80 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              Rådgivning
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#C0DACB] leading-relaxed">
              Er dit træ en risiko – eller bare til gene? Få en professionel og ærlig vurdering fra en erfaren fagmand.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="tel:42169230"
                className="inline-flex items-center gap-2.5 bg-[#D9691E] hover:bg-[#C25B15] text-white px-6 py-3.5 rounded-xl font-bold text-sm sm:text-base shadow-lg transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 42 16 92 30</span>
              </a>
              <a
                href="#kontakt-formular"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-xl font-semibold text-sm sm:text-base border border-white/20 transition-all whitespace-nowrap"
              >
                <span>Få faglig vurdering</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section (Verbatim scrape prose) */}
      <section className="py-16 sm:py-24 bg-[#F2EDE2]/70 border-y border-[#E2D8C8] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Image Side: Bredere og højere, placeret bag ved tekstboksen med overlap */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
              <div className="relative rounded-2xl overflow-hidden h-[420px] sm:h-[500px] lg:h-[620px] w-full">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/540320308-772834888876748-8893434418948825966-n.jpeg",
                    1200,
                    85
                  )}
                  alt="Besigtigelse af træets sundhed"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content Side: Boks med baggrundsfarve #1e5d31, ingen shadows eller border, overlapper billedet foran */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10 bg-[#1e5d31] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 shadow-none border-0">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                Vurdering af træets tilstand og sikkerhed
              </h2>

              {/* Exact scraped text */}
              <div className="space-y-4 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                <p>
                  Jeg tilbyder vurdering af træer, hvor du er i tvivl om træets tilstand eller måske frygter at det vælter eller grene brækker af.
                </p>
                <p className="font-semibold text-white bg-white/10 p-4 rounded-xl border border-white/15">
                  Jeg råder og vejleder til den bedste løsning, der nødvendigvis ikke altid er at fælde træet.
                </p>
                <p>
                  Rådgivningen udgør en faglig vurdering baseret på de forhold, der er synlige og tilgængelige på besigtigelsestidspunktet.
                </p>
                <p className="text-sm text-[#C0DACB] italic pt-2">
                  Der påtages ikke ansvar for skjulte forhold eller efterfølgende ændringer i træets tilstand.
                </p>
              </div>

              {/* Practical Guidance Checklist */}
              <div className="p-6 rounded-2xl bg-white/10 border border-white/15 space-y-3 mt-4">
                <h3 className="font-bold text-base text-white">Hvornår bør du få et træ vurderet?</h3>
                <ul className="space-y-2 text-sm text-[#D5E5DB]">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#F39C5A] mt-1 shrink-0" aria-hidden="true" />
                    <span>Tegn på svamp, råd eller hule partier i stamme eller rodhals.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#F39C5A] mt-1 shrink-0" aria-hidden="true" />
                    <span>Store, tørre eller døde grene i kronen over tag eller gangarealer.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#F39C5A] mt-1 shrink-0" aria-hidden="true" />
                    <span>Træet hælder unormalt eller løfter jorden omkring rødderne.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#F39C5A] mt-1 shrink-0" aria-hidden="true" />
                    <span>Tvivl om hvorvidt en beskæring er tilstrækkelig frem for fuld fældning.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION WITH FORM */}
      <section className="py-16 sm:py-24 bg-[#14291E] text-white relative overflow-hidden" id="kontakt-formular">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0 space-y-6 lg:pr-8">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight lg:max-w-[75%]">
                Er du i tvivl om et træs tilstand?
              </h2>
              <p className="text-base text-[#BFD9CA] leading-relaxed max-w-lg lg:max-w-[75%]">
                Send et billede eller en beskrivelse af træet. Jeg giver en professionel vurdering og hjælper dig med den tryggeste løsning.
              </p>

              {/* Direct Info Cards - unified single box overlapping slightly under form to the right */}
              <div className="bg-[#1C3B2C] p-6 sm:p-7 rounded-2xl border border-[#2B543F] space-y-4 relative z-0 lg:-mr-8 lg:pr-14 shadow-none">
                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Ring direkte
                    </span>
                    <a
                      href="tel:42169230"
                      className="text-lg font-extrabold text-white hover:text-[#F39C5A] transition-colors whitespace-nowrap"
                    >
                      42 16 92 30
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-regular fa-envelope text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Send en email
                    </span>
                    <a
                      href="mailto:info@casperstraeservice.dk"
                      className="text-base font-bold text-white hover:text-[#F39C5A] transition-colors whitespace-nowrap"
                    >
                      info@casperstraeservice.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-location-dot text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Adresse
                    </span>
                    <span className="text-sm font-medium text-white block">
                      Caspers Træservice • Folevej 23, 6510 Gram
                    </span>
                    <span className="text-xs text-[#8DB59E]">CVR: 37232521</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10">
              <ContactForm defaultService="Rådgivning" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
